import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { Plus, X } from "react-feather";
import { C, selectStyles } from "../reporting/reportingStyles";
import {
    hintStyle as kitHint, warnStyle as kitWarn, labelTextStyle, smallInputStyle, inputStyle,
    secondaryBtnStyle, withDisabled,
} from "../common/FormKit";

/**
 * Per-category and per-product rates for a membership tier.
 *
 * The tier's own discount is the DEFAULT. A rule here replaces it for one category or one
 * product, and the API resolves each cart line as: product rule, then category rule, then
 * the default. The filters above still win: an excluded product, or a category outside the
 * eligible list, gets nothing whatever its rule says. The API refuses such a rule, and this
 * editor flags it first so the admin is not surprised by the refusal.
 *
 * A rule's "SAR" amount is PER UNIT: "5 SAR off each latte". That differs from the tier's
 * own fixed amount, which comes off the order once.
 *
 * The form owns the whole list and sends it on every save. An empty list clears it.
 */

const SCOPES = [
    { scope: "category", title: "Category rates", noun: "category", addLabel: "Add category rate" },
    { scope: "product", title: "Product rates", noun: "product", addLabel: "Add product rate" },
];

const hintStyle = kitHint;
const warnStyle = kitWarn;

// One rule per row: target, type, value, remove. A grid rather than reactstrap columns so the
// row keeps its proportions inside a narrow section card.
const ruleRowStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(200px, 2.4fr) minmax(150px, 1.4fr) minmax(90px, 0.8fr) 36px",
    gap: 8,
    alignItems: "start",
    marginBottom: 8,
};

const removeBtnStyle = {
    ...secondaryBtnStyle,
    width: 36,
    height: 36,
    padding: 0,
    justifyContent: "center",
};

// The select sits beside 36px inputs, so its control is the same height.
const ruleSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({ ...selectStyles.control(base, state), minHeight: 36, borderRadius: 8, borderWidth: 1.5 }),
};

/**
 * Why a rule could never fire, or null if it can. The client copy of the API's
 * findDeadDiscountRules (membershipUtil.js): the filters run before the rates, so a rate on
 * something the filters remove is saved but never applied.
 *
 *   excluded             product rate on a product in Excluded products
 *   productCategory      product rate on a product whose category is outside a restricted list
 *   category             category rate on a category outside a restricted list
 *
 * `productCategoryById` is { [productId]: CategoryId } from the product listing. A product
 * missing from it is not judged here; the API still refuses it on save.
 *
 * One function for both the save check and the per-row warning, so the two cannot drift.
 */
export function deadRuleReason(rule, {
    excludedProductIds = [],
    applyToAllCategories = true,
    eligibleCategoryIds = [],
    productCategoryById = {},
} = {}) {
    if (!rule?.targetId) return null;
    const restricted = applyToAllCategories === false && (eligibleCategoryIds || []).length > 0;

    if (rule.scope === "product") {
        if ((excludedProductIds || []).includes(rule.targetId)) return "excluded";
        const categoryId = productCategoryById?.[rule.targetId];
        if (restricted && categoryId !== undefined && categoryId !== null
            && !eligibleCategoryIds.includes(Number(categoryId))) {
            return "productCategory";
        }
    } else if (rule.scope === "category") {
        if (restricted && !eligibleCategoryIds.includes(rule.targetId)) return "category";
    }
    return null;
}

const nameOf = (rule, noun) => (rule.targetName ? `"${rule.targetName}"` : `This ${noun}`);

const DEAD_RULE_SAVE_MESSAGE = {
    excluded: (rule) => `${nameOf(rule, "product")} has a rate but is also in Excluded products. Remove one: the exclusion would win.`,
    productCategory: (rule) => `${nameOf(rule, "product")} has a rate, but its category is not in Eligible categories, so the rate would never apply.`,
    category: (rule) => `${nameOf(rule, "category")} has a rate but is not in Eligible categories, so the rate would never apply.`,
};

const DEAD_RULE_WARNING = {
    excluded: "This product is excluded above, so this rate would never apply.",
    productCategory: "This product's category is not in Eligible categories, so this rate would never apply.",
    category: "This category is not in Eligible categories, so this rate would never apply.",
};

/** Client-side copy of the API's checks, so a bad rule is caught before the round trip. */
export function validateDiscountRules(rules, context = {}) {
    const seen = new Set();
    for (const rule of rules || []) {
        if (!rule.targetId) return `Pick a ${rule.scope} for every ${rule.scope} rate, or remove the empty row.`;

        const value = Number(rule.discountValue);
        if (!Number.isFinite(value) || value <= 0) return "Every rate needs a value greater than zero.";
        if (rule.discountType === "percentage" && value > 100) return "A percentage rate cannot be more than 100%.";

        const key = `${rule.scope}:${rule.targetId}`;
        if (seen.has(key)) return `The same ${rule.scope} has two rates. Keep one.`;
        seen.add(key);

        const reason = deadRuleReason(rule, context);
        if (reason) return DEAD_RULE_SAVE_MESSAGE[reason](rule);
    }
    return null;
}

const DiscountRulesEditor = ({
    rules,
    onChange,
    categoryOptions,
    productOptions,
    isDisabled,
    excludedProductIds,
    applyToAllCategories,
    eligibleCategoryIds,
    productCategoryById,
    defaultLabel,
}) => {
    const optionsFor = (scope) => (scope === "category" ? categoryOptions : productOptions);

    const rowsByScope = useMemo(() => {
        const out = { category: [], product: [] };
        (rules || []).forEach((rule, index) => {
            if (out[rule.scope]) out[rule.scope].push({ rule, index });
        });
        return out;
    }, [rules]);

    const update = (index, patch) => onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    const remove = (index) => onChange(rules.filter((_, i) => i !== index));
    const add = (scope) => onChange([
        ...(rules || []),
        { scope, targetId: null, discountType: "percentage", discountValue: "" },
    ]);

    const warningFor = (rule) => {
        const reason = deadRuleReason(rule, {
            excludedProductIds,
            applyToAllCategories,
            eligibleCategoryIds,
            productCategoryById,
        });
        return reason ? DEAD_RULE_WARNING[reason] : null;
    };

    return (
        <div>
            <p style={{ ...hintStyle, marginTop: 0, marginBottom: 12 }}>
                Give chosen categories or products their own rate. Everything else gets the tier's
                default ({defaultLabel}). A <strong>product rate beats its category's rate</strong>, and
                both beat the default. A SAR rate is taken off <strong>each unit</strong>, never more
                than the item costs.
            </p>

            {SCOPES.map(({ scope, title, noun, addLabel }) => {
                const options = optionsFor(scope);
                const used = new Set((rules || []).filter((r) => r.scope === scope).map((r) => r.targetId));

                const addDisabled = isDisabled || used.size >= options.length;
                return (
                    <div
                        key={scope}
                        style={{ border: `1px solid ${C.borderLight}`, borderRadius: 10, padding: 14, marginBottom: 12, background: C.bg }}
                    >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <span style={{ ...labelTextStyle, marginBottom: 0 }}>{title}</span>
                            <button
                                type="button"
                                onClick={() => add(scope)}
                                disabled={addDisabled}
                                style={withDisabled({ ...secondaryBtnStyle, padding: "6px 12px", fontSize: 12 }, addDisabled)}
                            >
                                <Plus size={13} aria-hidden /> {addLabel}
                            </button>
                        </div>

                        {rowsByScope[scope].length === 0 && (
                            <div style={{ ...hintStyle, marginTop: 0 }}>
                                No {noun} rates. Every {noun} uses the default.
                            </div>
                        )}

                        {rowsByScope[scope].length > 0 && (
                            <div style={{ ...ruleRowStyle, marginBottom: 4 }} aria-hidden>
                                <span style={{ ...labelTextStyle, marginBottom: 0 }}>{noun}</span>
                                <span style={{ ...labelTextStyle, marginBottom: 0 }}>Rate type</span>
                                <span style={{ ...labelTextStyle, marginBottom: 0 }}>Value</span>
                                <span />
                            </div>
                        )}

                        {rowsByScope[scope].map(({ rule, index }) => {
                            const selected = options.find((o) => o.value === rule.targetId)
                                // A rule whose target was deleted since: show it, so the admin can remove it.
                                || (rule.targetId ? { value: rule.targetId, label: rule.targetName || `Deleted ${noun} #${rule.targetId}` } : null);
                            const warning = warningFor(rule);
                            const isPct = rule.discountType === "percentage";

                            return (
                                <div key={`${scope}-${index}`} style={ruleRowStyle}>
                                    <div style={{ minWidth: 0 }}>
                                        <Select
                                            styles={ruleSelectStyles}
                                            options={options.filter((o) => o.value === rule.targetId || !used.has(o.value))}
                                            value={selected}
                                            onChange={(opt) => update(index, { targetId: opt ? opt.value : null, targetName: opt?.label })}
                                            placeholder={`Search ${noun}...`}
                                            isDisabled={isDisabled}
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            aria-label={`${noun} for rate ${index + 1}`}
                                        />
                                        {warning && <span style={{ ...warnStyle, marginTop: 4 }}>{warning}</span>}
                                    </div>
                                    <select
                                        value={rule.discountType}
                                        onChange={(e) => update(index, { discountType: e.target.value })}
                                        disabled={isDisabled}
                                        aria-label="Rate type"
                                        style={{ ...inputStyle, ...smallInputStyle, height: 36 }}
                                    >
                                        <option value="percentage">Percentage off</option>
                                        <option value="amount">SAR off each unit</option>
                                    </select>
                                    <input
                                        type="number"
                                        min="0"
                                        step={isPct ? "1" : "0.01"}
                                        max={isPct ? 100 : undefined}
                                        value={rule.discountValue}
                                        onChange={(e) => update(index, { discountValue: e.target.value })}
                                        placeholder={isPct ? "20" : "5.00"}
                                        disabled={isDisabled}
                                        aria-label="Rate value"
                                        style={{ ...smallInputStyle, height: 36 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        disabled={isDisabled}
                                        aria-label={`Remove ${noun} rate ${index + 1}`}
                                        title="Remove"
                                        style={withDisabled(removeBtnStyle, isDisabled)}
                                    >
                                        <X size={14} aria-hidden />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

DiscountRulesEditor.propTypes = {
    /** [{ scope: "category"|"product", targetId, discountType, discountValue, targetName? }] */
    rules: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    categoryOptions: PropTypes.array.isRequired,
    productOptions: PropTypes.array.isRequired,
    isDisabled: PropTypes.bool,
    excludedProductIds: PropTypes.array,
    applyToAllCategories: PropTypes.bool,
    eligibleCategoryIds: PropTypes.array,
    /** { [productId]: CategoryId } from the product listing. */
    productCategoryById: PropTypes.object,
    /** e.g. "10% off" — shown so the admin knows what an unruled item gets. */
    defaultLabel: PropTypes.string,
};

export default DiscountRulesEditor;
